import { useStats } from "@src/presentation/hooks/stats"
import { Skeleton } from "@heroui/react"
import { usePermission } from "@src/presentation/hooks/usePermission"
import { PermissionPlaceholder } from "@src/presentation/components/PermissionPlaceholder"

export function StatsGrid() {
    const { hasPermission } = usePermission()
    const canViewStats = hasPermission("stats:read")

    const { data: stats, isLoading, error } = useStats({
        refetchInterval: 30000, // Auto-refresh every 30 seconds
        staleTime: 10000,
        enabled: canViewStats, // Only fetch when user has permission
    })

    // Early return if no permission - prevents component from mounting
    if (!canViewStats) {
        return <PermissionPlaceholder title="Statistics Dashboard" description="You need 'stats:read' permission to view statistics." />
    }

    return (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {/* Total PASCOs */}
            <div className="rounded bg-card hover:shadow-custom border border-border p-4 transition-colors hover:border-primary/50">
                <div className="flex items-center justify-between">
                    <div className="space-y-1">
                        <p className="text-sm font-medium text-muted-foreground">Total PASCOs</p>
                        {isLoading ? (
                            <>
                                <Skeleton className="h-9 w-16 rounded-lg" />
                                <Skeleton className="h-4 w-32 rounded-lg" />
                            </>
                        ) : error ? (
                            <>
                                <p className="text-3xl font-bold text-destructive">--</p>
                                <p className="text-xs text-destructive">Failed to load</p>
                            </>
                        ) : (
                            <>
                                <p className="text-xl md:text-3xl font-bold text-foreground">{stats?.pasco.total_pascos ?? 0}</p>
                                <p className="text-xs text-muted-foreground">{stats?.pasco.status_breakdown.completed ?? 0} processed</p>
                            </>
                        )}
                    </div>
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                        <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                            />
                        </svg>
                    </div>
                </div>
            </div>

            {/* Approved PASCOs */}
            <div className="rounded bg-card hover:shadow-custom border border-border p-4 transition-colors hover:border-green-500/50">
                <div className="flex items-center justify-between">
                    <div className="space-y-1">
                        <p className="text-sm font-medium text-muted-foreground">Approved PASCOs</p>
                        {isLoading ? (
                            <>
                                <Skeleton className="h-9 w-16 rounded-lg" />
                                <Skeleton className="h-4 w-32 rounded-lg" />
                            </>
                        ) : error ? (
                            <>
                                <p className="text-3xl font-bold text-destructive">--</p>
                                <p className="text-xs text-destructive">Failed to load</p>
                            </>
                        ) : (
                            <>
                                <p className="text-xl md:text-3xl font-bold text-green-600">{stats?.pasco.approved ?? 0}</p>
                                <p className="text-xs text-muted-foreground">Ready for use</p>
                            </>
                        )}
                    </div>
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-500/10">
                        <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                    </div>
                </div>
            </div>

            {/* Pending Approval PASCOs */}
            <div className="rounded bg-card hover:shadow-custom border border-border p-4 transition-colors hover:border-yellow-500/50">
                <div className="flex items-center justify-between">
                    <div className="space-y-1">
                        <p className="text-sm font-medium text-muted-foreground">Pending Approval</p>
                        {isLoading ? (
                            <>
                                <Skeleton className="h-9 w-16 rounded-lg" />
                                <Skeleton className="h-4 w-32 rounded-lg" />
                            </>
                        ) : error ? (
                            <>
                                <p className="text-3xl font-bold text-destructive">--</p>
                                <p className="text-xs text-destructive">Failed to load</p>
                            </>
                        ) : (
                            <>
                                <p className="text-xl md:text-3xl font-bold text-yellow-600">{stats?.pasco.pending_approval ?? 0}</p>
                                <p className="text-xs text-muted-foreground">Awaiting review</p>
                            </>
                        )}
                    </div>
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-yellow-500/10">
                        <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                    </div>
                </div>
            </div>

            {/* Completed PASCOs */}
            <div className="rounded bg-card hover:shadow-custom border border-border p-4 transition-colors hover:border-green-500/50">
                <div className="flex items-center justify-between">
                    <div className="space-y-1">
                        <p className="text-sm font-medium text-muted-foreground">Completed PASCOs</p>
                        {isLoading ? (
                            <>
                                <Skeleton className="h-9 w-16 rounded-lg" />
                                <Skeleton className="h-4 w-32 rounded-lg" />
                            </>
                        ) : error ? (
                            <>
                                <p className="text-3xl font-bold text-destructive">--</p>
                                <p className="text-xs text-destructive">Failed to load</p>
                            </>
                        ) : (
                            <>
                                <p className="text-xl md:text-3xl font-bold text-green-600">{stats?.pasco.status_breakdown.completed ?? 0}</p>
                                <p className="text-xs text-muted-foreground">Successfully processed</p>
                            </>
                        )}
                    </div>
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-500/10">
                        <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                    </div>
                </div>
            </div>

            {/* Total Users */}
            <div className="rounded border hover:shadow-custom border-border bg-card p-4 transition-colors hover:border-primary/50">
                <div className="flex items-center justify-between">
                    <div className="space-y-1">
                        <p className="text-sm font-medium text-muted-foreground">Total Users</p>
                        {isLoading ? (
                            <>
                                <Skeleton className="h-9 w-16 rounded-lg" />
                                <Skeleton className="h-4 w-24 rounded-lg" />
                            </>
                        ) : error ? (
                            <>
                                <p className="text-3xl font-bold text-destructive">--</p>
                                <p className="text-xs text-destructive">Failed to load</p>
                            </>
                        ) : (
                            <>
                                <p className="text-3xl font-bold text-foreground">{stats?.dashboard.total_users ?? 0}</p>
                                <p className="text-xs text-muted-foreground">{stats?.dashboard.active_users ?? 0} active</p>
                            </>
                        )}
                    </div>
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                        <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                            />
                        </svg>
                    </div>
                </div>
            </div>

            {/* Total Roles */}
            <div className="rounded border hover:shadow-custom border-border bg-card p-4 transition-colors hover:border-primary/50">
                <div className="flex items-center justify-between">
                    <div className="space-y-1">
                        <p className="text-sm font-medium text-muted-foreground">Total Roles</p>
                        {isLoading ? (
                            <>
                                <Skeleton className="h-9 w-16 rounded-lg" />
                                <Skeleton className="h-4 w-32 rounded-lg" />
                            </>
                        ) : error ? (
                            <>
                                <p className="text-3xl font-bold text-destructive">--</p>
                                <p className="text-xs text-destructive">Failed to load</p>
                            </>
                        ) : (
                            <>
                                <p className="text-3xl font-bold text-foreground">{stats?.dashboard.total_roles ?? 0}</p>
                                <p className="text-xs text-muted-foreground">{stats?.dashboard.total_permissions ?? 0} permissions</p>
                            </>
                        )}
                    </div>
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent/10">
                        <svg className="h-6 w-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                            />
                        </svg>
                    </div>
                </div>
            </div>

            {/* Total Jobs */}
            <div className="rounded border hover:shadow-custom border-border bg-card p-4 transition-colors hover:border-primary/50">
                <div className="flex items-center justify-between">
                    <div className="space-y-1">
                        <p className="text-sm font-medium text-muted-foreground">Total Jobs</p>
                        {isLoading ? (
                            <>
                                <Skeleton className="h-9 w-16 rounded-lg" />
                                <Skeleton className="h-4 w-32 rounded-lg" />
                            </>
                        ) : error ? (
                            <>
                                <p className="text-3xl font-bold text-destructive">--</p>
                                <p className="text-xs text-destructive">Failed to load</p>
                            </>
                        ) : (
                            <>
                                <p className="text-3xl font-bold text-foreground">{stats?.pasco_jobs.total_jobs ?? 0}</p>
                                <p className="text-xs text-muted-foreground">{stats?.pasco_jobs.status_breakdown.completed ?? 0} completed</p>
                            </>
                        )}
                    </div>
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                        <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                            />
                        </svg>
                    </div>
                </div>
            </div>
        </div>
    )
}